/**
 * 9-slice panels, repainted as **one** bitmap — the way an engine does it.
 *
 * ## Why this exists — measured 2026-09-08
 *
 * The panels are 9-sliced with CSS's own primitive, `border-image`, and every
 * slice number in `index.html` is that sprite's `m_Border` read out of Unity.
 * None of that changes here; this module only changes what draws it.
 *
 * Unity's `Image` in Sliced mode builds **one mesh**: 16 vertices, 9 quads, one
 * draw call. Two neighbouring quads share the very same vertices — the right
 * edge of the left quad *is* the left edge of the middle one, the same float —
 * and the rasteriser's fill rule covers each pixel on that edge exactly once.
 * A gap is structurally impossible. Cocos does the same.
 *
 * Chromium does not build a mesh. It issues **nine separate image draws**, each
 * antialiased against its own rectangle. Where the shared edge lands on a
 * fractional device pixel the left piece covers `a` of it and the right piece
 * `b`, and the two composite as `a + b - ab`, which never reaches 1 — so a
 * fraction of the backdrop shows through, as a one-pixel line down every cut.
 * Measured on `?settings=1`: flat band 239,228,198, seam column 205,194,169,
 * backdrop 21,19,44 — one leak fraction across all three channels, i.e.
 * coverage, not colour.
 *
 * `index.html` carries three CSS defences, and all three are mitigation:
 *
 *  1. the `@supports (width: round(1px, 1px))` block snaps every sliced box
 *     onto the device grid — real (~180 headless captures clean), but it needs
 *     a browser with CSS `round()` and a `--dp` that reaches the box;
 *  2. no sliced box sits inside a `transform`, so Chromium may snap the quads
 *     itself — also real, also not enough;
 *  3. two opaque bars behind each cut, in the sprite's own middle colour, which
 *     take the worst measured case from -43.7 to -0.5.
 *
 * None of them can reach a **page-level scale** — DevTools' device toolbar at
 * anything but 100% puts the whole document inside a transform, so the page's
 * pixel grid is not the device's. Measured at ratio 1.25, dip by page scale:
 * 0.80 -> -43.7, 0.86 -> -24.4, 0.92 -> -6.8, 0.97 -> -37.6, 1.03 -> -15.8.
 * And at 0.92 the old `translate(-50%,-50%)` centring reads -6.8 while the new
 * negative-margin centring reads -6.8 — identical, i.e. geometry is not what
 * decides that case at all.
 *
 * So this module does what the engine does: **one draw**. It reads the
 * `border-image` back off the computed style — source, slice, `fill` and the
 * four border widths — paints those nine pieces into a canvas at the element's
 * device-pixel size with **integer, exactly adjacent** destination rects, and
 * hands the result back as a single `background-image` under the border. There
 * is no interior edge left for anything to leak through, at any ratio, any
 * zoom, any page scale.
 *
 * ⚠️ **Nothing here duplicates a number.** The CSS keeps its `border-image`
 * declaration: it is the source this reads, it is what paints before the bitmap
 * is ready or if this module never runs, and it is where `nineSliceSnap.test.ts`
 * checks the slices against the sprite assets.
 *
 * On an element the bitmap then **replaces** it — `hideCssLayer` takes the
 * source down once the blob is installed. The first version left it on top as a
 * belt-and-braces second copy, and that was a mistake with a visible cost: the
 * CSS layer is opaque, so it was the only thing on screen, and Chromium's
 * `border-image` resamples worse than canvas at `high` (0.425 vs 0.196 edge
 * roughness on the title cap, 2.93:1). See `hideCssLayer`. Pseudo-elements,
 * which no inline style can reach, still keep the CSS layer and `clearRim`.
 */

/** What a `border-image` says, in the order CSS writes a box: top right bottom left. */
export interface SliceSpec {
  /** Resolved `border-image-source` URL. */
  src: string
  /** `border-image-slice`, in **source** pixels. */
  slice: [number, number, number, number]
  /** Whether the shorthand carried `fill` — i.e. whether the middle is drawn. */
  fill: boolean
  /** The four border widths, in CSS pixels. */
  border: [number, number, number, number]
}

/** Expand a 1-, 2-, 3- or 4-value CSS box shorthand to [top, right, bottom, left]. */
export function boxOf(values: number[]): [number, number, number, number] {
  const [a, b = a, c = a, d = b] = values
  return [a, b, c, d]
}

/**
 * Read the `border-image` off a computed style.
 *
 * Returns `null` for anything this has no business repainting: no source, a
 * gradient rather than a URL, or a percentage slice (every panel here is
 * authored in source pixels, and `m_Border` is a pixel count).
 */
export function readSpec(style: CSSStyleDeclaration): SliceSpec | null {
  const source = style.borderImageSource
  if (!source || source === 'none') return null
  const url = /url\(["']?([^"')]+)["']?\)/.exec(source)
  if (!url) return null

  const rawSlice = style.borderImageSlice
  if (rawSlice.includes('%')) return null
  const fill = rawSlice.includes('fill')
  const numbers = rawSlice
    .replace('fill', '')
    .trim()
    .split(/\s+/)
    .map(Number)
    .filter((n) => Number.isFinite(n))
  if (!numbers.length) return null

  const border = boxOf(
    [style.borderTopWidth, style.borderRightWidth, style.borderBottomWidth, style.borderLeftWidth].map(
      parseFloat,
    ),
  )
  return { src: url[1], slice: boxOf(numbers), fill, border }
}

/**
 * The nine destination rects, in **device pixels**, for a box of `w` x `h`.
 *
 * Integers that tile exactly: the middle takes whatever the rounded edges leave
 * over, so no two pieces overlap and no gap can open between them. That is the
 * whole point of this file — it is the property nine separate `border-image`
 * quads do not have once the box is off the device grid.
 *
 * `f` is CSS's own reduction (Backgrounds 3 §6.2): when the border widths do
 * not fit the box, **one** factor scales all four, which is what the browser
 * was already doing to the rule this reads.
 */
export function edgeSizes(
  w: number,
  h: number,
  border: [number, number, number, number],
  dpr: number,
): { top: number; right: number; bottom: number; left: number } {
  const [t, r, b, l] = border.map((v) => v * dpr)
  const f = Math.min(1, t + b > 0 ? h / (t + b) : 1, l + r > 0 ? w / (l + r) : 1)
  const top = Math.min(Math.round(t * f), h)
  const bottom = Math.min(Math.round(b * f), h - top)
  const left = Math.min(Math.round(l * f), w)
  const right = Math.min(Math.round(r * f), w - left)
  return { top, right, bottom, left }
}

/** Draw the nine pieces into a context already sized to `w` x `h` device pixels. */
export function drawSlices(
  ctx: CanvasRenderingContext2D,
  img: CanvasImageSource,
  sw: number,
  sh: number,
  w: number,
  h: number,
  spec: SliceSpec,
  dpr: number,
): void {
  const [st, sr, sb, sl] = spec.slice
  const { top, right, bottom, left } = edgeSizes(w, h, spec.border, dpr)
  const midW = w - left - right
  const midH = h - top - bottom
  const smw = sw - sl - sr
  const smh = sh - st - sb

  const piece = (
    sx: number,
    sy: number,
    spw: number,
    sph: number,
    dx: number,
    dy: number,
    dw: number,
    dh: number,
  ) => {
    if (spw <= 0 || sph <= 0 || dw <= 0 || dh <= 0) return
    ctx.drawImage(img, sx, sy, spw, sph, dx, dy, dw, dh)
  }

  piece(0, 0, sl, st, 0, 0, left, top)
  piece(sw - sr, 0, sr, st, w - right, 0, right, top)
  piece(0, sh - sb, sl, sb, 0, h - bottom, left, bottom)
  piece(sw - sr, sh - sb, sr, sb, w - right, h - bottom, right, bottom)
  piece(sl, 0, smw, st, left, 0, midW, top)
  piece(sl, sh - sb, smw, sb, left, h - bottom, midW, bottom)
  piece(0, st, sl, smh, 0, top, left, midH)
  piece(sw - sr, st, sr, smh, w - right, top, right, midH)
  // The middle only when the shorthand said `fill`, same as CSS.
  if (spec.fill) piece(sl, st, smw, smh, left, top, midW, midH)
}

/**
 * Erase the bitmap's outermost ring, so only the `border-image` above it draws
 * the box's outer edge.
 *
 * ⚠️ The bitmap and the `border-image` are the same art in the same place, and
 * where both are **opaque** drawing twice changes nothing. The outer rim is not
 * opaque: a sprite's edge is antialiased, and compositing alpha `a` over itself
 * gives `1 - (1 - a)^2`, which is strictly more. So the rim came out darker and
 * fatter than the CSS it was supposed to be invisible behind — the toggle
 * buttons grew a heavy black outline, which is what the user saw. Nothing wants
 * a backing out there: the seam lives on the **interior** slice cuts, and the
 * outer edge has only ever been one quad.
 *
 * A whole device pixel each side is enough — the rim is one antialiased pixel —
 * and it costs the top few pixels of the vertical cuts, which run hundreds.
 */
export function clearRim(ctx: CanvasRenderingContext2D, w: number, h: number, dpr: number): void {
  const rim = Math.max(1, Math.round(dpr))
  if (w <= rim * 2 || h <= rim * 2) return
  ctx.clearRect(0, 0, w, rim)
  ctx.clearRect(0, h - rim, w, rim)
  ctx.clearRect(0, 0, rim, h)
  ctx.clearRect(w - rim, 0, rim, h)
}

/**
 * The sliced pseudo-elements, and the custom property each one's bitmap goes
 * into.
 *
 * ⚠️ A pseudo-element has no `style` object to write to, so its bitmap has to
 * arrive through an inherited custom property — which means every one needs its
 * **own** name. `--nineSrc` set on `.toggleButton` would inherit straight down
 * into `.toggleKnob::before` and paint the track's bitmap on the knob.
 */
const PSEUDO: { selector: string; pseudo: '::before' | '::after'; property: string }[] = [
  { selector: '.popup .popupScrim', pseudo: '::after', property: '--nineScrim' },
  { selector: '#settingsPopup .toggleButton', pseudo: '::before', property: '--nineToggleTrack' },
  { selector: '#settingsPopup .toggleSlider', pseudo: '::before', property: '--nineSliderBase' },
  { selector: '#settingsPopup .toggleKnob', pseudo: '::before', property: '--nineKnob' },
]

interface Target {
  el: HTMLElement
  pseudo: '::before' | '::after' | null
  property: string | null
  spec: SliceSpec
  url: string | null
  w: number
  h: number
}

const targets = new Map<HTMLElement, Target[]>()

const sprites = new Map<string, Promise<HTMLImageElement>>()

function loadSprite(src: string): Promise<HTMLImageElement> {
  let pending = sprites.get(src)
  if (!pending) {
    pending = new Promise((resolve, reject) => {
      const img = new Image()
      img.onload = () => resolve(img)
      img.onerror = () => reject(new Error(`nine-slice sprite failed: ${src}`))
      img.src = src
    })
    sprites.set(src, pending)
  }
  return pending
}

function toBlobUrl(canvas: HTMLCanvasElement): Promise<string> {
  return new Promise((resolve) => {
    if (typeof canvas.toBlob !== 'function') return resolve(canvas.toDataURL())
    canvas.toBlob((blob) => resolve(blob ? URL.createObjectURL(blob) : canvas.toDataURL()))
  })
}

/**
 * Read the box's `border-image` back **from the stylesheet**, not from the
 * inline `none` this module writes once its bitmap is up.
 *
 * ⚠️ `paint` re-reads the spec on every repaint on purpose — the knob and the
 * buy button swap sprite at the same size, and a cached spec left the old
 * colour in the bitmap. Hiding the CSS layer would destroy exactly that read,
 * because the computed source is then `none` forever. So the override comes
 * off for the length of one `getComputedStyle` and goes straight back on;
 * nothing is painted in between, and `getComputedStyle` recomputes style
 * synchronously, so the value read is the stylesheet's.
 *
 * A pseudo-element cannot be reached by an inline style at all, so its read is
 * never affected by this — see `paint` for why pseudos keep the CSS layer.
 */
export function readLiveSpec(target: Target): SliceSpec | null {
  const override = target.el.style.borderImageSource
  if (override) target.el.style.borderImageSource = ''
  const spec = readSpec(getComputedStyle(target.el, target.pseudo))
  if (override) target.el.style.borderImageSource = override
  return spec
}

async function paint(target: Target, w: number, h: number): Promise<void> {
  const dpr = window.devicePixelRatio || 1
  const cw = Math.max(1, Math.round(w * dpr))
  const ch = Math.max(1, Math.round(h * dpr))
  if (w <= 0 || h <= 0) return
  /*
   * ⚠️ The spec is re-read, never cached past a repaint. Three of these boxes
   * **change sprite without changing size**: the settings knob swaps
   * `btn_1_red` for `btn_1_green` on toggle, and `.popupButton` is recoloured
   * per popup. Caching the first read left the old colour in the bitmap under
   * the border, so the leak showed the *previous* sprite — which is what a
   * seam on the ON switch looked like, and it was the one place the size-only
   * guard below could never notice.
   */
  const fresh = readLiveSpec(target)
  if (!fresh) return
  if (target.w === cw && target.h === ch && target.spec.src === fresh.src) return
  target.spec = fresh
  target.w = cw
  target.h = ch

  const img = await loadSprite(target.spec.src)
  const canvas = document.createElement('canvas')
  canvas.width = cw
  canvas.height = ch
  const ctx = canvas.getContext('2d')
  if (!ctx) return
  /*
   * ⚠️ `high`, not the default. Every piece here is **minified** — a corner of
   * `btn_1_base2` is 94 source pixels drawn into about 52 — and canvas's default
   * `low` quality is a plain bilinear tap, which at more than 2:1 skips texels
   * and breaks a thin dark outline into a blotchy one. Chromium's own
   * `border-image` path filters better **than that**, so the first version of
   * this module made the toggle buttons visibly grubbier than the CSS it
   * replaced: heavier, ragged black rims on the gold ring, worst at the corners.
   * User-reported, then read off a 4x A/B crop of the same button with
   * `?noslice=1`.
   *
   * At `high` the comparison turns around, which is what `hideCssLayer` below
   * is for: `high` resamples by area, `border-image` does not.
   *
   * ⚠️ Stepping the source down by halves first (512 -> 256 -> ...) is the
   * canvas folklore fix and it is **wasted work here**: measured on this very
   * sprite, one halving before the draw moved no channel by more than 1/255.
   * `high` already averages the whole footprint.
   */
  ctx.imageSmoothingEnabled = true
  ctx.imageSmoothingQuality = 'high'
  drawSlices(ctx, img, img.naturalWidth, img.naturalHeight, cw, ch, target.spec, dpr)
  // Only the boxes that keep the CSS layer double-draw their rim. See below.
  if (target.pseudo) clearRim(ctx, cw, ch, dpr)

  const url = await toBlobUrl(canvas)
  // A repaint that started earlier can land later; only the newest size counts.
  if (target.w !== cw || target.h !== ch) return URL.revokeObjectURL(url)
  const stale = target.url
  target.url = url
  if (target.property) target.el.style.setProperty(target.property, `url("${url}")`)
  else {
    target.el.style.backgroundImage = `url("${url}")`
    hideCssLayer(target)
  }
  if (stale?.startsWith('blob:')) URL.revokeObjectURL(stale)
}

/**
 * Take the `border-image` down once the bitmap is up, so the bitmap is what the
 * player actually sees.
 *
 * ## Why — measured 2026-09-08, user-reported on the settings title
 *
 * 🪤 "The title background is a bit jagged at both ends." It was, and it was
 * **not** the seam this module was written for: `?noslice=1` looked identical,
 * because the `border-image` paints *over* the bitmap and it is opaque. Whatever
 * the bitmap's quality, the CSS layer was the only thing reaching the screen.
 *
 * The caps are the worst minification in the UI. `Panel_Title_Blue.png` is
 * 512x256 with `m_Border {x: 199.92, z: 199.92}`, so each cap is **200 source
 * pixels drawn into `80u`** — about 68 px on the reported window, 2.93:1. The
 * middle is a one-dimensional stretch of a flat band, which is why the ends
 * were the only place it showed. Edge roughness of the outer outline (RMS of
 * the second difference of the sub-pixel edge position, 0 = a smooth curve),
 * measured on an isolated page at that exact geometry:
 *
 *     border-image                       0.425
 *     canvas drawImage, quality 'high'   0.196
 *
 * ⚠️ And the backdrop is part of that measurement, not decoration: the first
 * A/B was run on the dark `#2b2540` scrim, where a near-black outline on a
 * near-black ground hides its own staircase, and **both** rows read as smooth.
 * On the cream the title actually sits against, the difference above appears.
 *
 * ## Why only the elements
 *
 * A pseudo-element has no inline style, so `border-image-source` cannot be
 * taken off it from here — it would need a marker class in `index.html` plus a
 * guard against the `class` MutationObserver below re-entering. Those four
 * boxes (the scrim and the three switch parts, all under 2:1) therefore keep
 * the CSS layer on top and keep `clearRim` with it. Elements do not: a single
 * layer has no second rim to composite against, and erasing it would clip the
 * sprite's own antialiased edge.
 *
 * The bitmap only takes over once it exists, so first paint, a browser without
 * `ResizeObserver`, a sprite that fails to load and `?noslice=1` all still get
 * the CSS panel — this line is never reached in any of those.
 */
function hideCssLayer(target: Target): void {
  target.el.style.borderImageSource = 'none'
  /*
   * 🪤 And the border underneath it. Taking the image away does not leave the
   * border empty — it uncovers the plain `border-style: solid` the sliced boxes
   * all carry, painted in `border-color`, which is `currentColor` here and so
   * comes out the popup's near-black `#1e2747`. Measured before this line: the
   * settings title rendered as a bare blue band with a 62 px dark bar over each
   * cap, i.e. exactly the art this module had just drawn correctly, covered up.
   *
   * ⚠️ Two headless full-page captures of that state came back looking **right**,
   * because the screenshot landed before the async blob was installed. The bug
   * only showed once the page was asked over CDP rather than photographed —
   * `background-image` said `blob:`, `border-image-source` said `none`, and the
   * blob decoded on its own canvas had both caps. §8's rule about photographs
   * again: an image proves what was on screen at an instant nobody chose.
   *
   * `transparent`, not `border-width: 0`: the width is layout — every absolutely
   * positioned child of these panels is placed from the padding box.
   */
  target.el.style.borderColor = 'transparent'
}

/**
 * The pseudo's own **border** box, in CSS px.
 *
 * ⚠️ `getComputedStyle().width` / `.height` is the **content** box — in Chrome
 * that is true whatever `box-sizing` says, and a pseudo-element gets the
 * initial `content-box` anyway, because `*` does not match pseudos. The
 * bitmap is painted with `background-origin: border-box`, so handing that
 * content size to `paint` sizes the canvas to the hole *inside* the border
 * and `background-size: 100% 100%` then stretches it back over the whole box.
 *
 * On the settings switch the borders are the whole box: 25 + 25 against a 52
 * u-tall track leaves **2 px** of content, so the track's bitmap was drawn 57
 * x 2 and smeared to 127 x 52 — a flat rectangle with no rounded corners at
 * all, which is what the user saw. Measured at 562 x 1000: the sliced left
 * edge held a dead-straight x = 309 for all 56 rows, while `?noslice=1` (the
 * CSS `border-image` alone) rounded 323 -> 310 over eleven rows.
 *
 * Padding is added too, for the same reason; every sliced pseudo here has
 * none, but the border box is what the background paints into either way.
 */
export function pseudoBox(el: HTMLElement, pseudo: string): [number, number] {
  const style = getComputedStyle(el, pseudo)
  const px = (value: string) => parseFloat(value) || 0
  const width =
    px(style.width) +
    px(style.borderLeftWidth) +
    px(style.borderRightWidth) +
    px(style.paddingLeft) +
    px(style.paddingRight)
  const height =
    px(style.height) +
    px(style.borderTopWidth) +
    px(style.borderBottomWidth) +
    px(style.paddingTop) +
    px(style.paddingBottom)
  return [width, height]
}

let observer: ResizeObserver | null = null

function repaintFor(el: HTMLElement, w: number, h: number): void {
  for (const target of targets.get(el) ?? []) {
    if (target.pseudo) {
      const [pw, ph] = pseudoBox(el, target.pseudo)
      void paint(target, pw, ph)
    } else {
      void paint(target, w, h)
    }
  }
}

/**
 * Take over every 9-sliced box under `root`, and keep it repainted.
 *
 * Safe to call again for a subtree added later; anything already taken over is
 * skipped. Sizes come from the `ResizeObserver` entry's border box rather than
 * `getBoundingClientRect`, because a frame mid-animation is inside a `scale()`
 * and the rect would report the scaled size.
 */
export function mountNineSlices(root: ParentNode = document): void {
  if (typeof ResizeObserver === 'undefined') return
  observer ??= new ResizeObserver((entries) => {
    for (const entry of entries) {
      const box = entry.borderBoxSize?.[0]
      repaintFor(
        entry.target as HTMLElement,
        box ? box.inlineSize : entry.contentRect.width,
        box ? box.blockSize : entry.contentRect.height,
      )
    }
  })

  const add = (el: HTMLElement, target: Target) => {
    const list = targets.get(el)
    if (list) list.push(target)
    else targets.set(el, [target])
  }

  for (const node of root.querySelectorAll<HTMLElement>('*')) {
    if (targets.has(node)) continue
    const own = readSpec(getComputedStyle(node))
    if (own) add(node, { el: node, pseudo: null, property: null, spec: own, url: null, w: -1, h: -1 })
    for (const { selector, pseudo, property } of PSEUDO) {
      if (!node.matches(selector)) continue
      const spec = readSpec(getComputedStyle(node, pseudo))
      if (spec) add(node, { el: node, pseudo, property, spec, url: null, w: -1, h: -1 })
    }
    if (targets.has(node)) observer.observe(node)
  }
  watchClassChanges()
}

let classWatcher: MutationObserver | null = null

/**
 * A `class` change can swap a sliced box's sprite without touching its size —
 * `.toggleButton.on` recolours the knob, `#noAdsPopup .popupButton` the button.
 * A `ResizeObserver` cannot see that, so watch the attribute as well and let
 * `paint` decide: it re-reads the spec and only redraws when the source moved.
 */
function watchClassChanges(): void {
  if (classWatcher || typeof MutationObserver === 'undefined') return
  classWatcher = new MutationObserver(() => {
    for (const [el, list] of targets) {
      for (const target of list) {
        if (target.pseudo) {
          const [pw, ph] = pseudoBox(el, target.pseudo)
          void paint(target, pw, ph)
        } else {
          void paint(target, el.offsetWidth, el.offsetHeight)
        }
      }
    }
  })
  classWatcher.observe(document.documentElement, {
    subtree: true,
    attributes: true,
    attributeFilter: ['class'],
  })
}

/**
 * Redraw everything at the new ratio when the display scale changes.
 *
 * There is no `devicepixelratiochange` event, so this re-arms a `(resolution:
 * Ndppx)` query built from the *current* ratio — the same trick the `--dp`
 * script in `index.html`'s head uses, and for the same reason.
 */
export function watchPixelRatio(): void {
  if (typeof matchMedia !== 'function') return
  const arm = () => {
    const dpr = window.devicePixelRatio || 1
    matchMedia(`(resolution: ${dpr}dppx)`).addEventListener(
      'change',
      () => {
        for (const [el, list] of targets) {
          for (const target of list) {
            target.w = -1
            target.h = -1
          }
          repaintFor(el, el.offsetWidth, el.offsetHeight)
        }
        arm()
      },
      { once: true },
    )
  }
  arm()
}
